import { z } from 'zod';
import { commaSeparated } from '../common/comma-separated';
import { DeletedBySchema } from '../common/deleted-by.schema';
import { IncludeTestWorkspacesQuerySchema } from '../admin/test-workspaces.schemas';
import { ContactListItemSchema } from '../relationship/contacts.schemas';
import { IdsFilterQuerySchema } from '../common/ids-filter.schema';
import { DealLossReasonSchema } from '../deal/deals.schemas';
import { PropertyLinksSchema } from '../common/links.schemas';
import { ListSearchTermSchema } from '../common/search-term.schema';
import { TRANSACTION_TYPES, TransactionTypeSchema, type TransactionType } from '../common/transaction-type.schema';
import {
  PropertyCategorySchema,
  PropertySubtypeSchema,
  isValidCategorySubtype,
  type PropertyCategory,
  type PropertySubtype,
} from '../common/property-taxonomy.schema';
import {
  CommercialFineTypeSchema,
  isValidCommercialFineType,
  type CommercialFineType,
} from '../common/commercial-fine-type.schema';
import { refersTo } from '../common/refers-to';
import { COMMISSION_AMOUNT_MAX, CommissionBasisSchema, CommissionStatementSchema } from './buyer-commission';

// A property's marketing type is the shared SALE / RENTAL axis (see
// `common/transaction-type.schema`); a deal's `type` references the same set.
export const MARKETING_TYPES = TRANSACTION_TYPES;
// `IN_ACQUISITION` (IMM-171) is the second pre-ACTIVE staging bucket: a
// Lead-qualified property (IMM-193 Qualify) lands here, not in DRAFT, and is
// hidden from the default Active tab the same way DRAFT is. Promotion to
// ACTIVE is owned by IMM-171's deal.contract-received subscriber.
export const PROPERTY_STATUSES = ['DRAFT', 'IN_ACQUISITION', 'ACTIVE', 'INACTIVE', 'ARCHIVED'] as const;

// The two pre-ACTIVE staging states (IMM-171.4). A member may create a property
// in either, and toggle a property freely between them — no publish gate applies
// (the gate runs only on promotion to ACTIVE). ACTIVE / INACTIVE / ARCHIVED are
// reached through the dedicated lifecycle routes.
export const STAGING_STATUSES = ['DRAFT', 'IN_ACQUISITION'] as const;

// IMM-392 — Archivierungsgrund. The reason an Objekt was archived. Wire tokens
// are en-US UPPER_CASE (ADR 0009); the German labels live in `packages/i18n`
// keyed by these tokens. `SOLD` is offered only on SALE listings and `RENTED`
// only on RENTAL ones (gated in the domain aggregate); the other six apply to
// any marketing type. `OTHER` requires a free-text note.
export const ARCHIVE_REASONS = [
  'SOLD',
  'RENTED',
  'TRANSACTED_EXTERNALLY',
  'WITHDRAWN',
  'MANDATE_EXPIRED',
  'MANDATE_TERMINATED',
  'DUPLICATE',
  'OTHER',
] as const;
export const ArchiveReasonSchema = z.enum(ARCHIVE_REASONS);
export type ArchiveReason = z.infer<typeof ArchiveReasonSchema>;

export const MarketingTypeSchema = TransactionTypeSchema;
export const PropertyStatusSchema = z.enum(PROPERTY_STATUSES);
export const StagingStatusSchema = z.enum(STAGING_STATUSES);

export type MarketingType = TransactionType;
export type PropertyStatus = z.infer<typeof PropertyStatusSchema>;
export type StagingStatus = z.infer<typeof StagingStatusSchema>;

// The Objekt's Immobilientyp is the shared two-level taxonomy (IMM-432.3): a
// physical `category` (always present — an Objekt always has a type) plus an
// optional controlled `subtype`. A subtype is only valid alongside its owning
// category, so the create / edit bodies pair-check it via `superRefine` below.
// `category` being required is the one difference from the Lead's adoption
// (IMM-432.2), where a first-touch lead may carry no type at all.
function refinePropertyImmobilientyp(
  value: {
    readonly category?: PropertyCategory | undefined;
    readonly subtype?: PropertySubtype | null | undefined;
    readonly commercialFineType?: CommercialFineType | null | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  if (
    value.subtype !== undefined &&
    value.subtype !== null &&
    (value.category === undefined || !isValidCategorySubtype(value.category, value.subtype))
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['subtype'],
      message: `Subtype ${value.subtype} does not belong to category ${value.category ?? 'null'}`,
    });
  }

  const fineType = value.commercialFineType;

  if (fineType === undefined || fineType === null) return;

  if (value.subtype !== undefined) {
    if (!isValidCommercialFineType(value.subtype, fineType)) {
      ctx.addIssue({
        code: 'custom',
        path: ['commercialFineType'],
        message: `Fine type ${fineType} does not belong to subtype ${value.subtype ?? 'null'}`,
      });
    }

    return;
  }

  if (value.category !== undefined && value.category !== 'COMMERCIAL') {
    ctx.addIssue({
      code: 'custom',
      path: ['commercialFineType'],
      message: `Fine type ${fineType} requires category COMMERCIAL`,
    });
  }
}

// Section enums — Appendix A of the doc, en-US.

export const CONDITION_VALUES = [
  'FIRST_OCCUPATION',
  'LIKE_NEW',
  'WELL_MAINTAINED',
  'MODERNISED',
  'PARTIALLY_RENOVATED',
  'FULLY_RENOVATED',
  'PARTIALLY_FULLY_RENOVATED',
  'NEEDS_PARTIAL_FULL_RENOVATION',
  'NEEDS_RENOVATION',
  'DILAPIDATED',
  'BY_ARRANGEMENT',
] as const;
export const ConditionSchema = z.enum(CONDITION_VALUES);
export type Condition = z.infer<typeof ConditionSchema>;

export const INTERIOR_QUALITY_VALUES = ['STANDARD', 'UPSCALE', 'LUXURY'] as const;
export const InteriorQualitySchema = z.enum(INTERIOR_QUALITY_VALUES);
export type InteriorQuality = z.infer<typeof InteriorQualitySchema>;

export const CONSTRUCTION_PHASE_VALUES = ['PROJECTED', 'UNDER_CONSTRUCTION', 'COMPLETED'] as const;
export const ConstructionPhaseSchema = z.enum(CONSTRUCTION_PHASE_VALUES);
export type ConstructionPhase = z.infer<typeof ConstructionPhaseSchema>;

export const HEATING_TYPE_VALUES = ['STOVE', 'FLOOR_LEVEL', 'CENTRAL', 'DISTRICT', 'UNDERFLOOR'] as const;
export const HeatingTypeSchema = z.enum(HEATING_TYPE_VALUES);
export type HeatingType = z.infer<typeof HeatingTypeSchema>;

export const FIRING_TYPE_VALUES = [
  'OIL',
  'GAS',
  'LIQUID_GAS',
  'NATURAL_GAS_LIGHT',
  'NATURAL_GAS_HEAVY',
  'ELECTRIC',
  'ALTERNATIVE',
  'SOLAR',
  'GEOTHERMAL',
  'AIR_HEAT_PUMP',
  'DISTRICT',
  'DISTRICT_STEAM',
  'LOCAL_HEAT',
  'HEAT_DELIVERY',
  'BLOCK_CHP',
  'WATER_ELECTRIC',
  'PELLET',
  'WOOD',
  'COAL',
  'BIOENERGY',
  'WIND_ENERGY',
  'WATER_ENERGY',
  'ENVIRONMENTAL_HEAT',
] as const;
export const FiringTypeSchema = z.enum(FIRING_TYPE_VALUES);
export type FiringType = z.infer<typeof FiringTypeSchema>;

export const ENERGY_CERTIFICATE_BASIS_VALUES = ['DEMAND', 'CONSUMPTION'] as const;
export const EnergyCertificateBasisSchema = z.enum(ENERGY_CERTIFICATE_BASIS_VALUES);
export type EnergyCertificateBasis = z.infer<typeof EnergyCertificateBasisSchema>;

export const ENERGY_CERTIFICATE_YEAR_VALUES = [
  'EN_2008',
  'EN_2014',
  'GMODG_2027',
  'WITHOUT',
  'NOT_REQUIRED',
  'AT_VIEWING',
] as const;
export const EnergyCertificateYearSchema = z.enum(ENERGY_CERTIFICATE_YEAR_VALUES);
export type EnergyCertificateYear = z.infer<typeof EnergyCertificateYearSchema>;

/**
 * IMM-612 — the two `certificateYear` values that assert no certificate
 * document exists. Selecting either is a complete statement on its own.
 */
export const ENERGY_CERTIFICATE_YEARS_WITHOUT_DOCUMENT = ['WITHOUT', 'NOT_REQUIRED'] as const;

/**
 * Whether a certificate document exists for this `certificateYear` — the single
 * owner of that rule. Every surface (contract refinement, dialog, detail page)
 * consumes this predicate rather than re-typing the membership test.
 *
 * `AT_VIEWING` is deliberately NOT a "no document" value: the certificate exists
 * and its data is known, only its presentation is deferred to the viewing.
 */
export function energyCertificateExists(year: EnergyCertificateYear): boolean {
  return !(ENERGY_CERTIFICATE_YEARS_WITHOUT_DOCUMENT as ReadonlyArray<string>).includes(year);
}

export const ENERGY_CLASS_VALUES = ['A_PLUS', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const;
export const EnergyClassSchema = z.enum(ENERGY_CLASS_VALUES);
export type EnergyClass = z.infer<typeof EnergyClassSchema>;

/** BGBl. 2026 I Nr. 226, Art. 2: an Ausweis issued from this date carries the GModG set (new § 87). */
export const GMODG_CERTIFICATES_ISSUED_FROM = '2027-01-01';

export const isGmodgIssueDate = (issuedAt: string | null): boolean =>
  issuedAt !== null && issuedAt >= GMODG_CERTIFICATES_ISSUED_FROM;

/** The single owner of "is this a GModG-Ausweis" — the issue date decides, not the stored label. */
export const isGmodgCertificate = (
  energy: {
    readonly certificateYear: EnergyCertificateYear;
    readonly certificateIssuedAt: string | null;
  } | null,
): boolean =>
  energy !== null && energyCertificateExists(energy.certificateYear) && isGmodgIssueDate(energy.certificateIssuedAt);

/** The label immoOS stores: a certificate issued from 2027 is `GMODG_2027`, every other year is kept. */
export const deriveCertificateYear = (year: EnergyCertificateYear, issuedAt: string | null): EnergyCertificateYear =>
  energyCertificateExists(year) && isGmodgIssueDate(issuedAt) ? 'GMODG_2027' : year;

export const NON_RESIDENTIAL_ENERGY_CLASS_VALUES = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
] as const satisfies ReadonlyArray<EnergyClass>;
export const ENERGY_CLASS_SCALES = ['A_PLUS_TO_H', 'A_TO_G'] as const;
export type EnergyClassScale = (typeof ENERGY_CLASS_SCALES)[number];

/** Anlage 10a: a Nichtwohngebäude with a GModG-Ausweis is rated A–G; everything else keeps A+–H. */
export const energyClassScaleOf = (
  category: PropertyCategory,
  energy: Parameters<typeof isGmodgCertificate>[0],
): EnergyClassScale => (category === 'COMMERCIAL' && isGmodgCertificate(energy) ? 'A_TO_G' : 'A_PLUS_TO_H');

export const energyClassesOf = (scale: EnergyClassScale): ReadonlyArray<EnergyClass> =>
  scale === 'A_TO_G' ? NON_RESIDENTIAL_ENERGY_CLASS_VALUES : ENERGY_CLASS_VALUES;

/** The recorded class when it lies on the Objekt's scale, else `null` — every reader uses this, never `energyClass` raw. */
export const energyClassOnScale = (
  category: PropertyCategory,
  energy: (Parameters<typeof isGmodgCertificate>[0] & { readonly energyClass: EnergyClass | null }) | null,
): EnergyClass | null =>
  energy?.energyClass != null && energyClassesOf(energyClassScaleOf(category, energy)).includes(energy.energyClass)
    ? energy.energyClass
    : null;

export const PARKING_SPACE_TYPE_VALUES = [
  'GARAGE',
  'UNDERGROUND_GARAGE',
  'CARPORT',
  'OPEN_SPACE',
  'MULTI_STOREY_GARAGE',
  'DUPLEX',
  'OTHER',
] as const;
export const ParkingSpaceTypeSchema = z.enum(PARKING_SPACE_TYPE_VALUES);
export type ParkingSpaceType = z.infer<typeof ParkingSpaceTypeSchema>;

export const FLOORING_TYPE_VALUES = [
  'TILES',
  'STONE',
  'CARPET',
  'PARQUET',
  'ENGINEERED_PARQUET',
  'LAMINATE',
  'PLANKS',
  'PLASTIC',
  'SCREED',
  'RAISED_FLOOR',
  'LINOLEUM',
  'MARBLE',
  'TERRACOTTA',
  'GRANITE',
] as const;
export const FlooringTypeSchema = z.enum(FLOORING_TYPE_VALUES);
export type FlooringType = z.infer<typeof FlooringTypeSchema>;

export const KITCHEN_TYPE_VALUES = ['OPEN', 'PANTRY', 'EAT_IN', 'BUILT_IN'] as const;
export const KitchenTypeSchema = z.enum(KITCHEN_TYPE_VALUES);
export type KitchenType = z.infer<typeof KitchenTypeSchema>;

export const CELLAR_STATE_VALUES = ['YES', 'NO', 'PARTIAL'] as const;
export const CellarStateSchema = z.enum(CELLAR_STATE_VALUES);
export type CellarState = z.infer<typeof CellarStateSchema>;

export const PETS_POLICY_VALUES = ['ALLOWED', 'NOT_ALLOWED', 'NEGOTIABLE'] as const;
export const PetsPolicySchema = z.enum(PETS_POLICY_VALUES);
export type PetsPolicy = z.infer<typeof PetsPolicySchema>;

export const SITE_DEVELOPMENT_TYPE_VALUES = ['UNDEVELOPED', 'PARTIAL', 'FULL', 'LOCAL_STANDARD'] as const;
export const SiteDevelopmentTypeSchema = z.enum(SITE_DEVELOPMENT_TYPE_VALUES);
export type SiteDevelopmentType = z.infer<typeof SiteDevelopmentTypeSchema>;

export const SITE_CONSTRUCTIBLE_TYPE_VALUES = [
  'PARAGRAPH_34_NEIGHBOURHOOD',
  'PARAGRAPH_35_OUTSIDE_AREA',
  'B_PLAN',
  'NO_BUILDING_LAND',
] as const;
export const SiteConstructibleTypeSchema = z.enum(SITE_CONSTRUCTIBLE_TYPE_VALUES);
export type SiteConstructibleType = z.infer<typeof SiteConstructibleTypeSchema>;

export const GERMAN_STATE_VALUES = [
  'BADEN_WUERTTEMBERG',
  'BAYERN',
  'BERLIN',
  'BRANDENBURG',
  'BREMEN',
  'HAMBURG',
  'HESSEN',
  'MECKLENBURG_VORPOMMERN',
  'NIEDERSACHSEN',
  'NORDRHEIN_WESTFALEN',
  'RHEINLAND_PFALZ',
  'SAARLAND',
  'SACHSEN',
  'SACHSEN_ANHALT',
  'SCHLESWIG_HOLSTEIN',
  'THUERINGEN',
] as const;
export const GermanStateSchema = z.enum(GERMAN_STATE_VALUES);
export type GermanState = z.infer<typeof GermanStateSchema>;

/**
 * The address as a **write** body: what a caller is allowed to submit. Every
 * value rule here — the 5-digit postcode, the non-empty city, the per-field
 * lengths — is a rule on *input*, and stays strict.
 *
 * Do NOT use this as a response shape (IMM-589). A stored Objekt is allowed to
 * be less complete than a submitted one: the lead-qualify gateway stages an
 * `IN_ACQUISITION` property with `postalCode: ''` when the lead carried none,
 * and `missingPublishFields()` treats exactly that blank as "not yet
 * publishable". Reading such a record through these rules fails the response
 * serializer and 500s the route. `PropertyDetailAddressSchema` below is the
 * read shape.
 */
export const PropertyAddressSchema = z.object({
  street: z.string().trim().min(1).max(100).nullable(),
  houseNumber: z.string().trim().min(1).max(20).nullable(),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/),
  city: z.string().trim().min(1).max(80),
  district: z.string().trim().min(1).max(80).nullable(),
  state: z.string().trim().min(1).max(80).nullable(),
  country: z.string().trim().min(1).max(80),
  parcelNumber: z.string().trim().min(1).max(40).nullable(),
  parcelSubdivision: z.string().trim().min(1).max(40).nullable(),
  hideExactAddress: z.boolean(),
  latitude: z.number().min(-90).max(90).nullable(),
  longitude: z.number().min(-180).max(180).nullable(),
});

export type PropertyAddress = z.infer<typeof PropertyAddressSchema>;

/**
 * The address as it appears on a **detail read** (IMM-589). Same keys, same
 * TypeScript types — only the value rules are dropped, because a response
 * schema may never be stricter than the state its producer can legitimately
 * hold. A pre-publish Objekt can carry a blank postcode or city (staged from a
 * lead), and a lead's own postcode is free text (`max(20)`, no format), so a
 * four-digit or non-German one reaches storage intact.
 *
 * The list shape (`PropertySummaryAddressSchema`) has always been unconstrained
 * on the same two fields, which is why the pipeline board rendered records the
 * detail read could not return.
 *
 * Built with `.extend()` rather than re-declared, so the two shapes cannot
 * drift apart on their **keys**: a field added to the write body appears here
 * automatically. If that new field carries a value rule, relax it here too —
 * inheriting a write rule onto the read side is the defect this split exists to
 * prevent.
 */
export const PropertyDetailAddressSchema = PropertyAddressSchema.extend({
  street: z.string().nullable(),
  houseNumber: z.string().nullable(),
  postalCode: z.string(),
  city: z.string(),
  district: z.string().nullable(),
  state: z.string().nullable(),
  country: z.string(),
  parcelNumber: z.string().nullable(),
  parcelSubdivision: z.string().nullable(),
});

export type PropertyDetailAddress = z.infer<typeof PropertyDetailAddressSchema>;

/**
 * Slimmed address surface used by **list** payloads. Carries only what
 * a row in a table needs to render — street + house number + postcode +
 * city. Detail payloads include the full `PropertyDetailAddressSchema`
 * (district, state, country, parcel*, hideExactAddress, lat/long).
 *
 * Both fields here are deliberately unconstrained, and always were — which is
 * why the board kept rendering records whose detail read 500d (IMM-589).
 */
export const PropertySummaryAddressSchema = z.object({
  street: z.string().min(1).nullable(),
  houseNumber: z.string().min(1).nullable(),
  postalCode: z.string(),
  city: z.string(),
});

export type PropertySummaryAddress = z.infer<typeof PropertySummaryAddressSchema>;

export const CreatePropertyAddressBodySchema = z.object({
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/),
  city: z.string().trim().min(1).max(80),
  street: z.string().trim().min(1).max(100).nullable().default(null),
  houseNumber: z.string().trim().min(1).max(20).nullable().default(null),
  district: z.string().trim().min(1).max(80).nullable().default(null),
  state: z.string().trim().min(1).max(80).nullable().default(null),
  country: z.string().trim().min(1).max(80).default('Deutschland'),
  parcelNumber: z.string().trim().min(1).max(40).nullable().default(null),
  parcelSubdivision: z.string().trim().min(1).max(40).nullable().default(null),
  hideExactAddress: z.boolean().default(false),
  latitude: z.number().min(-90).max(90).nullable().default(null),
  longitude: z.number().min(-180).max(180).nullable().default(null),
});

export const CreatePropertyPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

// Section sub-schemas. Each section maps 1:1 to a child table and is
// composed into the response (and may appear optionally on the create
// body for FE callers that want to populate sub-sections at creation).

// Canonical render order — read renderers (Areas section table) and
// editor dropdowns iterate this array so the UI order stays stable
// across slices. The enum schema mirrors it 1:1.
export const AREA_CODES = [
  'LIVING_AREA',
  'PLOT_AREA',
  'USABLE_AREA',
  'TOTAL_AREA',
  'NET_FLOOR_SPACE',
  // IMM-788.2 — the commercial use split + the reference areas it does not
  // govern. Appended after NET_FLOOR_SPACE per the pinned contract; the
  // array is the canonical render order both the section and the editor
  // iterate, so insertion position is load-bearing.
  'OFFICE_AREA',
  'RETAIL_AREA',
  'SHOP_AREA',
  'STORAGE_AREA',
  'HALL_AREA',
  'GASTRONOMY_AREA',
  'LETTABLE_AREA',
  'PARKING_SPACES',
  'ADDITIONAL_AREA',
  'BALCONY_AREA',
  'CUBATURE',
  'MIN_DIVISIBLE_AREA',
  'ROOMS',
  'BEDROOMS',
  'BATHROOMS',
  'FLOORS_TOTAL',
  'FLOOR',
] as const;
export const AreaCodeSchema = z.enum(AREA_CODES);
export type AreaCode = z.infer<typeof AreaCodeSchema>;
export const PropertyAreaEntrySchema = z.object({
  type: AreaCodeSchema,
  value: z.number().nonnegative(),
});
export type PropertyAreaEntry = z.infer<typeof PropertyAreaEntrySchema>;
export const PropertyAreasSchema = z.array(PropertyAreaEntrySchema);
export type PropertyAreasView = z.infer<typeof PropertyAreasSchema>;

// Write-only: carries the PARKING_SPACES integer rule (BL-039 — the response
// shape above stays unrefined so a stored non-integer row still reads).
export const PropertyAreaWriteEntrySchema = PropertyAreaEntrySchema.superRefine((entry, ctx) => {
  if (entry.type !== 'PARKING_SPACES' || Number.isInteger(entry.value)) return;

  ctx.addIssue({ code: 'custom', path: ['value'], message: 'PARKING_SPACES must be a whole number' });
});
export const PropertyAreasWriteSchema = z.array(PropertyAreaWriteEntrySchema);

export const PLACE_TYPE_VALUES = [
  'AIRPORT',
  'TRAIN_STATION',
  'METRO_STATION',
  'BUS_STOP',
  'TRAM_STOP',
  'MOTORWAY_EXIT',
  'HIGHWAY',
  'SCHOOL',
  'KINDERGARTEN',
  'HOSPITAL',
  'PHARMACY',
  'SUPERMARKET',
] as const;
export const PlaceTypeSchema = z.enum(PLACE_TYPE_VALUES);
export type PlaceType = z.infer<typeof PlaceTypeSchema>;

export const PropertyDistanceEntrySchema = z.object({
  placeType: PlaceTypeSchema,
  placeName: z.string().trim().min(1),
  distanceMeters: z.number().int().nonnegative(),
});
export type PropertyDistanceEntry = z.infer<typeof PropertyDistanceEntrySchema>;
export const PropertyDistancesSchema = z.array(PropertyDistanceEntrySchema);
export type PropertyDistancesView = z.infer<typeof PropertyDistancesSchema>;

// Spec §10 lists Stellplatzpreis among ETA Wohnen amenities; §11 service-charge /
// heating-costs / maintenance-reserve / rent-subsidy as rent incidentals. We
// pull them all into `property_additional_costs` (1-n) so the §10 / §11
// section tables hold only the core, single-instance pricing fields.
export const ADDITIONAL_COST_TYPES = [
  'SERVICE_CHARGE',
  'HEATING_COSTS',
  'MAINTENANCE_RESERVE',
  'RENT_SUBSIDY',
  'PARKING_SPACE_PRICE',
  'FURNITURE_SURCHARGE',
] as const;
export const AdditionalCostTypeSchema = z.enum(ADDITIONAL_COST_TYPES);
export type AdditionalCostType = z.infer<typeof AdditionalCostTypeSchema>;

// IMM-788.1 — whether a stated net price adds VAT or already includes it.
// `null` everywhere it appears is "not stated", a real state the publication
// gate (IMM-788.4) names rather than a value to default away.
export const VAT_TREATMENTS = ['NET_PLUS_VAT', 'VAT_INCLUSIVE'] as const;
export const VatTreatmentSchema = z.enum(VAT_TREATMENTS);
export type VatTreatment = z.infer<typeof VatTreatmentSchema>;

// IMM-788.1 — which half of the Miete/m² <-> Kaltmiete pair the agent typed.
// `UNQUOTED` is both cleared; a domain-only concern, the wire keeps the two
// independent nullable numbers (BL-039) — see `rentQuoteOf` in the backend
// domain and `rentQuotedAs` in `pricing-derivation.ts`.
export const RENT_QUOTED_AS = ['TOTAL', 'PER_SQM', 'UNQUOTED'] as const;
export const RentQuotedAsSchema = z.enum(RENT_QUOTED_AS);
export type RentQuotedAs = z.infer<typeof RentQuotedAsSchema>;

export const PropertyAdditionalCostEntrySchema = z.object({
  type: AdditionalCostTypeSchema,
  amount: z.number().nonnegative(),
  note: z.string().nullable(),
  perSqm: z.number().nonnegative().nullable().default(null),
  vatTreatment: VatTreatmentSchema.nullable().default(null),
});
export type PropertyAdditionalCostEntry = z.infer<typeof PropertyAdditionalCostEntrySchema>;

export const PropertyAdditionalCostsSchema = z.array(PropertyAdditionalCostEntrySchema);
export type PropertyAdditionalCostsView = z.infer<typeof PropertyAdditionalCostsSchema>;

export const PropertyBuildingSchema = z.object({
  yearBuilt: z.number().int(),
  lastRenovated: z.number().int(),
  condition: ConditionSchema,
  interiorQuality: InteriorQualitySchema,
  constructionPhase: ConstructionPhaseSchema,
});
export type PropertyBuildingView = z.infer<typeof PropertyBuildingSchema>;

/**
 * The five fields that describe the certificate document itself. They are
 * required exactly when `energyCertificateExists(certificateYear)`, and must be
 * `null` otherwise — see `refineEnergyCertificateCompleteness`.
 */
export const ENERGY_CERTIFICATE_FIELD_KEYS = [
  'certificateBasis',
  'energyClass',
  'energyValue',
  'certificateIssuedAt',
  'certificateValidUntil',
] as const;
export type EnergyCertificateFieldKey = (typeof ENERGY_CERTIFICATE_FIELD_KEYS)[number];

/** Which rule an energy refinement issue reports, carried on the issue's `params`. */
export type EnergyCertificateIssueRule = 'required' | 'must-be-absent' | 'gmodg-issue-date';

type EnergyCertificateShape = {
  readonly certificateYear: EnergyCertificateYear;
  readonly certificateBasis: EnergyCertificateBasis | null;
  readonly energyClass: EnergyClass | null;
  readonly energyValue: number | null;
  readonly includesWarmWater: boolean;
  readonly certificateIssuedAt: string | null;
  readonly certificateValidUntil: string | null;
};

/**
 * IMM-612 — the certificate's fields are conditional on `certificateYear`,
 * in BOTH directions:
 *
 * | `certificateYear`                  | the five certificate fields | `includesWarmWater` |
 * | ---------------------------------- | --------------------------- | ------------------- |
 * | `EN_2008`, `EN_2014`, `AT_VIEWING` | all required (non-null)     | any                 |
 * | `WITHOUT`, `NOT_REQUIRED`          | all must be `null`          | must be `false`     |
 *
 * Requiring `null` rather than merely allowing it is what stops a „Ohne Ausweis"
 * record from carrying a leftover class and date from before the switch — a state
 * no surface could render honestly. `includesWarmWater` qualifies `energyValue`;
 * with no value it must be `false`. Every issue is raised on its own field's
 * `path`, so a form maps it back to the field that caused it.
 */
export function refineEnergyCertificateCompleteness(value: EnergyCertificateShape, ctx: z.RefinementCtx): void {
  const addIssue = (key: string, rule: EnergyCertificateIssueRule, message: string) =>
    ctx.addIssue({ code: 'custom', path: [key], message, params: { rule } });

  if (energyCertificateExists(value.certificateYear)) {
    for (const key of ENERGY_CERTIFICATE_FIELD_KEYS) {
      if (value[key] === null) {
        addIssue(key, 'required', `${key} is required when a certificate exists for certificateYear`);
      }
    }

    return;
  }

  for (const key of ENERGY_CERTIFICATE_FIELD_KEYS) {
    if (value[key] !== null) {
      addIssue(key, 'must-be-absent', `${key} must be null when certificateYear states no certificate exists`);
    }
  }

  if (value.includesWarmWater) {
    addIssue(
      'includesWarmWater',
      'must-be-absent',
      'includesWarmWater must be false when certificateYear states no certificate exists',
    );
  }
}

export function refineGmodgCertificate(
  value: EnergyCertificateShape & { readonly primaryEnergyValue?: number | null | undefined },
  ctx: z.RefinementCtx,
): void {
  if (value.certificateYear === 'GMODG_2027' && !isGmodgIssueDate(value.certificateIssuedAt)) {
    ctx.addIssue({
      code: 'custom',
      path: ['certificateYear'],
      message: 'certificateYear GMODG_2027 requires a certificateIssuedAt on or after 2027-01-01',
      params: { rule: 'gmodg-issue-date' satisfies EnergyCertificateIssueRule },
    });
  }

  if (value.primaryEnergyValue != null && !isGmodgCertificate(value)) {
    ctx.addIssue({
      code: 'custom',
      path: ['primaryEnergyValue'],
      message: 'primaryEnergyValue must be null unless the certificate is a GModG certificate',
      params: { rule: 'must-be-absent' satisfies EnergyCertificateIssueRule },
    });
  }
}

const PropertyEnergyFieldsSchema = z.object({
  certificateBasis: EnergyCertificateBasisSchema.nullable(),
  certificateYear: EnergyCertificateYearSchema,
  energyClass: EnergyClassSchema.nullable(),
  energyValue: z.number().nullable(),
  includesWarmWater: z.boolean(),
  certificateIssuedAt: z.iso.date().nullable(),
  certificateValidUntil: z.iso.date().nullable(),
  heatingType: z.array(HeatingTypeSchema),
  firingType: z.array(FiringTypeSchema),
});

export const PropertyEnergySchema = PropertyEnergyFieldsSchema.extend({
  primaryEnergyValue: z.number().nullable(),
}).superRefine(refineEnergyCertificateCompleteness);

/** `primaryEnergyValue` omitted means "keep the stored value". */
export const PropertyEnergyWriteSchema = PropertyEnergyFieldsSchema.extend({
  primaryEnergyValue: z.number().min(0).max(99999.99).nullable().optional(),
}).superRefine((value, ctx) => {
  refineEnergyCertificateCompleteness(value, ctx);
  refineGmodgCertificate(value, ctx);
});
export type PropertyEnergyWriteBody = z.infer<typeof PropertyEnergyWriteSchema>;
export type PropertyEnergyView = z.infer<typeof PropertyEnergySchema>;

export const PropertyDescriptionsBundleSchema = z.object({
  description: z.string().nullable(),
  equipmentDescription: z.string().nullable(),
  locationDescription: z.string().nullable(),
  miscellaneous: z.string().nullable(),
});
export type PropertyDescriptionsBundle = z.infer<typeof PropertyDescriptionsBundleSchema>;
export const PropertyDescriptionsSchema = z.record(z.string(), PropertyDescriptionsBundleSchema);
export type PropertyDescriptionsView = z.infer<typeof PropertyDescriptionsSchema>;

// IMM-794.2 — the refusal threshold for a single description text, in UTF-16
// code units (the unit both the frontend counter and Postgres agree on without
// a shared helper). Spelled out in the German/English refusal copy rather than
// interpolated, so it is a fixed constant rather than an ICU number decision.
export const PROPERTY_DESCRIPTION_MAX_LENGTH = 20_000;

// IMM-1362.1 — IMM-225's caps, in UTF-16 code units like PROPERTY_DESCRIPTION_MAX_LENGTH.
export const PROPERTY_EXPOSE_TITLE_MAX_LENGTH = 120;
export const PROPERTY_HIGHLIGHT_MAX_LENGTH = 80;
// A nudge, never a limit (IMM-225 Resolved Decision 3).
export const PROPERTY_HIGHLIGHTS_SOFT_CAP = 5;

// The stored shape as READ. `version` is the row's optimistic-concurrency token
// (IMM-794): send it back as `expectedVersion` on the next write, and a save
// that would overwrite a colleague's concurrent edit is refused instead.
// Deliberately absent from the write bundle — a caller creating a property has
// nothing to be concurrent with.
export const PropertyDescriptionsResponseBundleSchema = PropertyDescriptionsBundleSchema.extend({
  version: z.number().int().positive(),
  exposeTitle: z.string().nullable(),
  highlights: z.array(z.string()),
});
export type PropertyDescriptionsResponseBundle = z.infer<typeof PropertyDescriptionsResponseBundleSchema>;
export const PropertyDescriptionsResponseSchema = z.record(z.string(), PropertyDescriptionsResponseBundleSchema);
export type PropertyDescriptionsResponseView = z.infer<typeof PropertyDescriptionsResponseSchema>;

export const UpdatePropertyDescriptionsPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});
export type UpdatePropertyDescriptionsPathParams = z.infer<typeof UpdatePropertyDescriptionsPathParamsSchema>;

// No `locale`: V1 writes `de-DE` only and the server fixes it, so a caller
// cannot address a locale the UI cannot show. No `.max()` on the four texts,
// deliberately — the 20 000-character rule is a DOMAIN refusal with its own
// code and its own German sentence, and a Zod boundary failure surfaces as
// `shared.request.invalid`, which the dictionary cannot key that sentence on.
// The DoS backstop is the app-wide body limit.
export const UpdatePropertyDescriptionsBodySchema = z
  .object({
    description: z.string().nullable(),
    equipmentDescription: z.string().nullable(),
    locationDescription: z.string().nullable(),
    miscellaneous: z.string().nullable(),
    exposeTitle: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Exposé-Titel: one line, at most 120 characters. null clears it; leave it out to keep the stored value.',
      ),
    highlights: z
      .array(z.string())
      .optional()
      .describe(
        'Highlights in display order, each at most 80 characters; blank entries are dropped. [] clears the list; leave it out to keep the stored value.',
      ),
    expectedVersion: z.number().int().positive().nullable(),
  })
  .strict();
export type UpdatePropertyDescriptionsBody = z.infer<typeof UpdatePropertyDescriptionsBodySchema>;

// Spec §10 Preis (Kauf) — read + create/update body share the same shape.
// All scalars nullable so a draft can hold any partial subset.
export const PropertyPricingSaleSchema = z.object({
  purchasePrice: z.number().nonnegative().nullable(),
  pricePerSqm: z.number().nonnegative().nullable(),
  priceOnInquiry: z.boolean(),
  hasCommission: z.boolean(),
  // The commission as free text, as entered before IMM-1168. Read-only: no write body carries it.
  commission: z.string().nullable(),
  commissionNote: z.string().nullable(),
  // IMM-788.1 — stated alongside `purchasePrice`, not instead of it; the
  // sale bundle's own VAT treatment, independent of the rental bundle's.
  netPurchasePrice: z.number().nonnegative().nullable().default(null),
  vatTreatment: VatTreatmentSchema.nullable().default(null),
  vatRate: z.number().min(0).max(100).nullable().default(null),
  commissionIncludesVat: z.boolean().default(false).meta({
    deprecated: true,
    description: 'Deprecated (IMM-1168): read-only, never published. The generated statement is always gross.',
  }),
  // IMM-1168: the buyer's commission, net of VAT. Only the value matching the basis is ever set.
  commissionBasis: CommissionBasisSchema.nullable().default(null),
  commissionRateNet: z.number().nullable().default(null),
  commissionAmountNet: z.number().nullable().default(null),
});
// The write body (update and create). Not `.strict()`: a stale `commission` or `commissionIncludesVat`
// from an old client is stripped, and the handler carries the stored values over. No range on the rate
// and no floor on the amount here: the domain refuses both with a code the dictionary keys its sentence on.
export const PropertyPricingSaleBodySchema = PropertyPricingSaleSchema.omit({
  commission: true,
  commissionIncludesVat: true,
}).extend({
  commissionAmountNet: z.number().max(COMMISSION_AMOUNT_MAX).nullable().default(null),
});
export type PropertyPricingSaleBody = z.input<typeof PropertyPricingSaleBodySchema>;
export type PropertyPricingSaleView = z.infer<typeof PropertyPricingSaleSchema>;

// The fields of the Preis (Kauf) section in their display order (IMM-1142).
export const PRICING_SALE_FIELDS = [
  'purchasePrice',
  'pricePerSqm',
  'netPurchasePrice',
  'priceOnInquiry',
  'hasCommission',
  'commissionBasis',
  'commissionRateNet',
  'commissionAmountNet',
  'commission',
  'commissionNote',
  'commissionIncludesVat',
  'vatTreatment',
  'vatRate',
] as const satisfies ReadonlyArray<keyof PropertyPricingSaleView>;
export const PricingSaleFieldSchema = z.enum(PRICING_SALE_FIELDS);
export type PricingSaleField = z.infer<typeof PricingSaleFieldSchema>;

// IMM-792.3 — Nebenkosten kind: whether the stated `ancillaryCosts` is a
// running advance payment (`ADVANCE`) or a flat monthly sum (`FLAT_RATE`).
// German labels live in `@immo/i18n` keyed by these tokens.
export const ANCILLARY_COSTS_TYPES = ['ADVANCE', 'FLAT_RATE'] as const;
export const AncillaryCostsTypeSchema = z.enum(ANCILLARY_COSTS_TYPES);
export type AncillaryCostsType = z.infer<typeof AncillaryCostsTypeSchema>;

// Spec §11 Preis (Miete) — the read view. IMM-792.3 widened this from the
// four shipped fields to the set a German letting is actually agreed in:
// Nebenkosten and its kind, Heizkosten, the deposit in both spellings, and
// the Stellplatzmiete. `totalRent` is stored exactly as sent, never derived
// on read. `deposit` is the legacy free-text deposit — still returned and
// rendered, but read-only from this slice on (see `PropertyPricingRentalBodySchema`).
//
// IMM-788 layers the commercial levers on top: the per-m² quote with its VAT
// treatment, the four negotiation incentives and price-on-inquiry.
// `baseRent` and `pricePerSqm` are mutually exclusive by DOMAIN invariant,
// not by this schema (BL-039): the wire keeps two independent nullable
// numbers so a stored row that somehow carries both still reads. The union
// lives in the backend domain's `RentQuote` / `rentQuoteOf`.
export const PropertyPricingRentalSchema = z.object({
  baseRent: z.number().nonnegative().nullable(),
  totalRent: z.number().nonnegative().nullable(),
  deposit: z.string().nullable(),
  heatingCostsIncluded: z.boolean(),
  pricePerSqm: z.number().nonnegative().nullable().default(null),
  vatTreatment: VatTreatmentSchema.nullable().default(null),
  vatRate: z.number().min(0).max(100).nullable().default(null),
  // IMM-788.3 — the four levers a commercial letting is negotiated on. Each is
  // independently optional: `null` is „not part of this offer", a different fact
  // from a stated `0`, and the two must stay distinguishable end to end (BL-001).
  compensationPayment: z.number().nonnegative().nullable().default(null), // compensation payment for fixtures the outgoing tenant leaves
  rentFreeMonths: z.number().int().nonnegative().nullable().default(null), // rent-free months
  fitOutContribution: z.number().nonnegative().nullable().default(null), // landlord contribution to fit-out works
  renewalOption: z.string().trim().max(256).nullable().default(null), // renewal option
  // IMM-788.4 — price on inquiry, mirroring the sale bundle's flag
  // exactly. `.default(false)` keeps every existing input literal valid.
  priceOnInquiry: z.boolean().default(false),
  // IMM-792.3 — the incidentals and deposit a German letting is agreed in.
  ancillaryCosts: z.number().nonnegative().nullable(),
  ancillaryCostsType: AncillaryCostsTypeSchema.nullable(),
  heatingCosts: z.number().nonnegative().nullable(),
  depositAmount: z.number().nonnegative().nullable(),
  depositMonths: z.number().nonnegative().max(24).nullable(),
  parkingRent: z.number().nonnegative().nullable(),
  // IMM-1057.2 — unlike the sale's non-null flag, `null` here means not stated.
  hasCommission: z.boolean().nullable().default(null),
  commission: z.string().nullable().default(null),
  commissionNote: z.string().nullable().default(null),
});
export type PropertyPricingRentalView = z.infer<typeof PropertyPricingRentalSchema>;

// Read-side only — never a request body (D1). Extends the write shape with
// the fields the server derives: which half was typed, the resolved other
// half, and the area basis the derivation used. A `null` basis or quote
// yields `null` derived figures, never `0` or an invented total.
export const PropertyPricingRentalResponseSchema = PropertyPricingRentalSchema.extend({
  quotedAs: RentQuotedAsSchema,
  derivedBaseRent: z.number().nullable(),
  derivedPricePerSqm: z.number().nullable(),
  basisArea: z.number().nullable(),
});
export type PropertyPricingRentalResponseView = z.infer<typeof PropertyPricingRentalResponseSchema>;

// IMM-792.4 — Mietkonditionen. German labels live in `@immo/i18n`.
export const FURNISHINGS = ['NONE', 'PARTIALLY_FURNISHED', 'FURNISHED'] as const;
export const FurnishingSchema = z.enum(FURNISHINGS);
export type Furnishing = z.infer<typeof FurnishingSchema>;

export const LEASE_TERMS = ['INDEFINITE', 'FIXED_TERM'] as const;
export const LeaseTermSchema = z.enum(LEASE_TERMS);
export type LeaseTerm = z.infer<typeof LeaseTermSchema>;

/** BL-049 — the one owner of "this lease term carries an end date". */
export function leaseTermHasEndDate(leaseTerm: LeaseTerm): boolean {
  return leaseTerm === 'FIXED_TERM';
}

export const RENT_TYPES = ['FIXED', 'GRADUATED', 'INDEXED'] as const;
export const RentTypeSchema = z.enum(RENT_TYPES);
export type RentType = z.infer<typeof RentTypeSchema>;

export const SUBLETTINGS = ['ALLOWED', 'NOT_ALLOWED', 'ON_REQUEST'] as const;
export const SublettingSchema = z.enum(SUBLETTINGS);
export type Subletting = z.infer<typeof SublettingSchema>;

/**
 * The calendar date in `Europe/Berlin`, used by both the body refinement and
 * the web `DateInput min` so server and form agree on what "today" is.
 * Berlin, not UTC: at 00:30 on 1 Oct in Berlin, UTC still says 30 Sep.
 */
export function isoDateToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

// IMM-792.4 — the read view. Deliberately loose, with no future-date rule and
// no upper bound (BL-039): a fixed term that ended last month is a
// legitimately stored state.
export const PropertyRentalTermsSchema = z.object({
  furnishing: FurnishingSchema,
  leaseTerm: LeaseTermSchema,
  fixedTermUntil: z.iso.date().nullable(),
  minimumTermMonths: z.number().int().nonnegative().nullable(),
  rentType: RentTypeSchema,
  subletting: SublettingSchema.nullable(),
});
export type PropertyRentalTermsView = z.infer<typeof PropertyRentalTermsSchema>;

/**
 * IMM-792.4 — the lease-term / end-date pairing: FIXED_TERM requires a
 * future-or-today `fixedTermUntil`; INDEFINITE requires it null.
 */
export function refineRentalTermsPairs(
  value: { readonly leaseTerm: LeaseTerm; readonly fixedTermUntil: string | null },
  ctx: z.RefinementCtx,
): void {
  if (leaseTermHasEndDate(value.leaseTerm)) {
    if (value.fixedTermUntil === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['fixedTermUntil'],
        message: 'fixedTermUntil is required when leaseTerm is FIXED_TERM',
      });
    } else if (value.fixedTermUntil < isoDateToday()) {
      ctx.addIssue({ code: 'custom', path: ['fixedTermUntil'], message: 'fixedTermUntil must be today or later' });
    }
  } else if (value.fixedTermUntil !== null) {
    ctx.addIssue({
      code: 'custom',
      path: ['fixedTermUntil'],
      message: 'fixedTermUntil must be null when leaseTerm is INDEFINITE',
    });
  }
}

// The write body for the Mietkonditionen section leaf. A separate object
// from the read view (BL-039), with every key required.
export const UpdatePropertyRentalTermsBodySchema = z
  .object({
    furnishing: FurnishingSchema,
    leaseTerm: LeaseTermSchema,
    fixedTermUntil: z.iso.date().nullable(),
    minimumTermMonths: z.number().int().nonnegative().max(600).nullable(),
    rentType: RentTypeSchema,
    subletting: SublettingSchema.nullable(),
  })
  .strict()
  .superRefine(refineRentalTermsPairs);
export type UpdatePropertyRentalTermsBody = z.infer<typeof UpdatePropertyRentalTermsBodySchema>;

/**
 * IMM-792.3 — the two cross-field rules on the rental price write body:
 * Heizkosten stated as „in Nebenkosten enthalten" must not also carry a
 * separate figure, and the deposit is one fact with two spellings, never both.
 */
export function refinePricingRentalPairs(
  value: {
    readonly heatingCostsIncluded: boolean;
    readonly heatingCosts: number | null;
    readonly depositAmount: number | null;
    readonly depositMonths: number | null;
  },
  ctx: z.RefinementCtx,
): void {
  if (value.heatingCostsIncluded && value.heatingCosts !== null) {
    ctx.addIssue({
      code: 'custom',
      path: ['heatingCosts'],
      message: 'heatingCosts must be null when heatingCostsIncluded is true',
    });
  }

  if (value.depositAmount !== null && value.depositMonths !== null) {
    ctx.addIssue({
      code: 'custom',
      path: ['depositMonths'],
      message: 'depositAmount and depositMonths cannot both be set',
    });
  }
}

// The write body for the rental price block (update and create both use
// it). Unlike the read view, it does not accept `deposit` — the legacy
// free-text deposit left the write surface in IMM-792.3 (PRD Decision 14)
// and is preserved by the handler from the stored aggregate instead. A
// stale `deposit` key sent by an old client is silently stripped, not
// refused, because the object is not `.strict()`.
export const PropertyPricingRentalBodySchema = z
  .object({
    baseRent: z.number().nonnegative().nullable(),
    totalRent: z.number().nonnegative().nullable(),
    heatingCostsIncluded: z.boolean(),
    ancillaryCosts: z.number().nonnegative().nullable().default(null),
    ancillaryCostsType: AncillaryCostsTypeSchema.nullable().default(null),
    heatingCosts: z.number().nonnegative().nullable().default(null),
    depositAmount: z.number().nonnegative().nullable().default(null),
    depositMonths: z.number().nonnegative().max(24).nullable().default(null),
    parkingRent: z.number().nonnegative().nullable().default(null),
    // IMM-788 — the commercial levers on the write surface. Every one is
    // defaulted, so a residential client that sends only the IMM-792.3 keys
    // stays valid and stores them as „not part of this offer".
    pricePerSqm: z.number().nonnegative().nullable().default(null),
    vatTreatment: VatTreatmentSchema.nullable().default(null),
    vatRate: z.number().min(0).max(100).nullable().default(null),
    compensationPayment: z.number().nonnegative().nullable().default(null),
    rentFreeMonths: z.number().int().nonnegative().nullable().default(null),
    fitOutContribution: z.number().nonnegative().nullable().default(null),
    renewalOption: z.string().trim().max(256).nullable().default(null),
    priceOnInquiry: z.boolean().default(false),
    // IMM-1057.2 — `null` means not stated, unlike the sale's non-null flag.
    hasCommission: z.boolean().nullable().default(null),
    commission: z.string().trim().max(64).nullable().default(null),
    commissionNote: z.string().nullable().default(null),
  })
  .superRefine(refinePricingRentalPairs);
export type PropertyPricingRentalBody = z.input<typeof PropertyPricingRentalBodySchema>;
export type PropertyPricingRentalBodyOutput = z.output<typeof PropertyPricingRentalBodySchema>;

// Read view: nullable sale or rental bundle depending on `marketing_type`.
export const PropertyPricingSchema = z.object({
  sale: PropertyPricingSaleSchema.nullable(),
  rental: PropertyPricingRentalResponseSchema.nullable(),
});
export type PropertyPricingView = z.infer<typeof PropertyPricingSchema>;

export const PropertyInvestmentYieldSchema = z.object({
  isRented: z.boolean(),
  rentalIncome: z.number(),
  rentalIncomeTarget: z.number(),
  rentalIncomeActual: z.number(),
  priceMultiplier: z.number(),
  numberOfApartments: z.number().int(),
  numberOfCommercialUnits: z.number().int(),
  occupancyRate: z.number(),
  numberOfVacancies: z.number().int(),
  tenantStructure: z.string(),
  walt: z.number(),
});
export type PropertyInvestmentYieldView = z.infer<typeof PropertyInvestmentYieldSchema>;

// Write shape: the `property_investment_yield` column bounds (numeric precision,
// the `>= 0` and 0–100 CHECKs). Without them an out-of-range value failed at the
// DB with an opaque 500 (IMM-1341 QA-9: Vermietungsquote 955). The read schema
// above stays lenient — the DB holds the same bounds, so nothing stored is wider.
export const PropertyInvestmentYieldWriteSchema = PropertyInvestmentYieldSchema.extend({
  rentalIncome: z.number().min(0).max(9_999_999_999.99),
  rentalIncomeTarget: z.number().min(0).max(9_999_999_999.99),
  rentalIncomeActual: z.number().min(0).max(9_999_999_999.99),
  priceMultiplier: z.number().min(-9_999.99).max(9_999.99),
  numberOfApartments: z.number().int().min(0).max(2_147_483_647),
  numberOfCommercialUnits: z.number().int().min(0).max(2_147_483_647),
  occupancyRate: z.number().min(0).max(100),
  numberOfVacancies: z.number().int().min(0).max(2_147_483_647),
  walt: z.number().min(-999.99).max(999.99),
});

export const PropertyPlotDetailsSchema = z.object({
  /** German GRZ (Grundflächenzahl, BauNVO §19): built footprint / plot area. */
  groundAreaRatio: z.number(),
  /** German GFZ (Geschossflächenzahl, BauNVO §20): total above-ground floor area / plot area. */
  floorAreaRatio: z.number(),
  /** German BMZ (Baumassenzahl, BauNVO §21): total enclosed cubic volume / plot area. */
  buildingMassRatio: z.number(),
  hasBuildingPermission: z.boolean(),
  shortTermBuildable: z.boolean(),
  requiresDemolition: z.boolean(),
  siteDevelopmentType: SiteDevelopmentTypeSchema,
  siteConstructibleType: SiteConstructibleTypeSchema,
});
export type PropertyPlotDetailsView = z.infer<typeof PropertyPlotDetailsSchema>;

// Canonical amenity code lists. The 1:1 source of truth — backend domain
// (`amenities-residential.ts` / `amenities-commercial.ts`) and the web-app
// editor both import these so adding a code only needs to happen here (and
// the matching label tables in `@immo/i18n`).
export const RESIDENTIAL_AMENITY_CODES = [
  'BALCONY',
  'GARDEN',
  'LIFT',
  'BARRIER_FREE',
  'GUEST_TOILET',
  'BUILT_IN_KITCHEN',
  'LISTED_MONUMENT',
  'LODGER_FLAT',
  'FLAT_SHARE_SUITABLE',
  'HOLIDAY_HOME',
  'REQUIRES_CERTIFICATE_OF_ELIGIBILITY',
  'LAN_CABLING',
  'AIR_CONDITIONING',
  'PARKING_SPACES',
  'NUMBER_OF_BALCONIES',
  'NUMBER_OF_TERRACES',
  'PARKING_SPACE_PRICE',
  'CELLAR_STATE',
  'PETS_POLICY',
  'PARKING_SPACE_TYPE',
  'FLOORING_TYPE',
] as const;
export const ResidentialAmenityCodeSchema = z.enum(RESIDENTIAL_AMENITY_CODES);
export type ResidentialAmenityCode = z.infer<typeof ResidentialAmenityCodeSchema>;

export const COMMERCIAL_AMENITY_CODES = [
  'GOODS_LIFT',
  'LOADING_RAMP',
  'HALL_HEIGHT',
  'GOODS_LIFT_LOAD',
  'FLOOR_LOAD',
  'CONNECTED_LOAD',
  'CRANE_RUNWAY_LOAD',
  'SHOP_WINDOW_LENGTH',
  'NUMBER_OF_SEATS',
  'NUMBER_OF_BEDS',
] as const;
export const CommercialAmenityCodeSchema = z.enum(COMMERCIAL_AMENITY_CODES);
export type CommercialAmenityCode = z.infer<typeof CommercialAmenityCodeSchema>;

export const PropertyAmenityEntrySchema = z.object({
  code: z.string(),
  value: z.string(),
});
export type PropertyAmenityEntry = z.infer<typeof PropertyAmenityEntrySchema>;
export const PropertyAmenitiesSchema = z.array(PropertyAmenityEntrySchema);

// Status is intentionally NOT on the create body — every new property
// lands in DRAFT per IMM-5 §AC and Resolved Decision #11. Lifecycle
// transitions (Aktiv / Inaktiv / Archiviert) happen on dedicated
// endpoints that enforce the publication-ready validation gate.
//
// Pricing is split by marketing_type: SALE properties carry `pricingSale`,
// RENTAL carry `pricingRental`. The opposite bundle is ignored. `additionalCosts`
// is open to both marketing types.
export const CreatePropertyBodySchema = z
  .object({
    title: z.string().trim().min(1).max(160),
    // IMM-432.3 — the shared Immobilientyp: `category` is required (an Objekt
    // always has a type), `subtype` optional. `nullish` so a caller may omit it
    // or send an explicit `null` (a cleared dependent Untertyp select). The pair
    // is validated by `refinePropertyImmobilientyp` below.
    category: PropertyCategorySchema,
    subtype: PropertySubtypeSchema.nullish(),
    marketingType: MarketingTypeSchema,
    // IMM-171.4 — optional staging status at create time. Omitted ⇒ DRAFT (the
    // long-standing default); `IN_ACQUISITION` stages the property directly. No
    // publish gate runs at creation for either value. Constrained to the two
    // pre-ACTIVE states — ACTIVE / INACTIVE / ARCHIVED are lifecycle-route-only.
    status: StagingStatusSchema.optional(),
    address: CreatePropertyAddressBodySchema,
    areas: PropertyAreasWriteSchema.optional(),
    distances: PropertyDistancesSchema.optional(),
    building: PropertyBuildingSchema.optional(),
    energy: PropertyEnergyWriteSchema.optional(),
    pricingSale: PropertyPricingSaleBodySchema.optional(),
    pricingRental: PropertyPricingRentalBodySchema.optional(),
    additionalCosts: PropertyAdditionalCostsSchema.optional(),
    investmentYield: PropertyInvestmentYieldWriteSchema.optional(),
    plotDetails: PropertyPlotDetailsSchema.optional(),
    descriptions: PropertyDescriptionsSchema.optional(),
    // Unified amenity list — the wire response splits this back into
    // `amenitiesResidential` / `amenitiesCommercial` by code-set, but on
    // the body the FE sends one list.
    amenities: PropertyAmenitiesSchema.optional(),
    // IMM-167 — optional reference number at create time. The
    // workspace-unique partial index backs uniqueness; the create handler
    // pre-checks and 422s `property.property.reference-number-taken` on a
    // collision. Omit it entirely to leave the property without a reference.
    referenceNumber: z.string().trim().min(1).max(64).nullish(),
    // IMM-6 slice 1 — Eigentümer attached on create. Optional and
    // workspace-scoped: the contact must belong to the same workspace +
    // organisation as the new property, otherwise the request 404s and
    // no property is written (the create runs inside a unit of work).
    ownerContactId: z.uuid().nullish(),
  })
  .superRefine(refinePropertyImmobilientyp);

export type CreatePropertyBody = z.infer<typeof CreatePropertyBodySchema>;

// The publish-gate field tokens (IMM-163). A property must carry all of
// these for its object type before it can go `ACTIVE`; the manual
// `activate` route fails 422 with the missing keys, and `PropertyResponse`
// surfaces the same gap read-side as `missingPublishFields`. The
// primary-area key depends on the object type (living / usable / total /
// plot area, or parking-space type).
export const PUBLISH_GATE_FIELDS = [
  'STREET',
  'HOUSE_NUMBER',
  'POSTAL_CODE',
  'CITY',
  'PURCHASE_PRICE',
  'BASE_RENT',
  'LIVING_AREA',
  'USABLE_AREA',
  'TOTAL_AREA',
  'PLOT_AREA',
  'PARKING_SPACE_TYPE',
  'VAT_TREATMENT', // IMM-788.4 — COMMERCIAL only
  'AVAILABLE_FROM',
  'ANCILLARY_COSTS_TYPE',
] as const;
export const PublishGateFieldSchema = z.enum(PUBLISH_GATE_FIELDS);
export type PublishGateField = z.infer<typeof PublishGateFieldSchema>;

// IMM-1145 — why an Aktiv Objekt is no longer freely available.
// RESERVED: marked by hand (IMM-1145.2), or a Kaufinteressent ACTIVE in RESERVED / NOTARY_APPOINTMENT.
// SOLD: a Kaufinteressent reached PURCHASED (status WON) while the Objekt is still ACTIVE.
export const PROPERTY_RESERVATION_STATES = ['RESERVED', 'SOLD'] as const;
export const PropertyReservationStateSchema = z.enum(PROPERTY_RESERVATION_STATES);
export type PropertyReservationState = z.infer<typeof PropertyReservationStateSchema>;

export const PropertyReservationSchema = z.object({
  state: PropertyReservationStateSchema,
  byHand: z.boolean(),
  throughSalesPipeline: z.boolean(),
});
export type PropertyReservation = z.infer<typeof PropertyReservationSchema>;

/**
 * Full property record on the wire. Returned by create + detail
 * endpoints; carries the full `PropertyDetailAddressSchema` plus all section
 * sub-views.
 */
export const PropertyResponseSchema = z.object({
  id: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: refersTo('organization', z.uuid()),
  assignedMemberId: refersTo('member', z.uuid()),
  title: z.string().min(1),
  // IMM-432.3 — the shared Immobilientyp. `category` is always present; the
  // controlled `subtype` is null when unset. Drives the readiness profile.
  category: PropertyCategorySchema,
  subtype: PropertySubtypeSchema.nullable(),
  // IMM-1057.2 — non-null only on a COMMERCIAL Objekt whose Untertyp has a list.
  commercialFineType: CommercialFineTypeSchema.nullable(),
  // IMM-551 — the Objekt-Nr.: read-only and system-assigned, the Standort-Präfix
  // followed by that Standort's counter (see `formatObjectNumber`). Present on
  // every Objekt, immutable for its life, and absent from every write body — a
  // submitted value is stripped by the contract rather than applied. Unrelated
  // to `referenceNumber` (the free-text Exposé-Nr.).
  objectNumber: z.string(),
  referenceNumber: z.string().nullable(),
  marketingType: MarketingTypeSchema,
  status: PropertyStatusSchema,
  // IMM-392 — the Archivierungsgrund + optional Notiz. Non-null only while the
  // property is ARCHIVED; both are cleared to null on un-archive. Legacy rows
  // archived before this feature carry a null reason.
  archiveReason: ArchiveReasonSchema.nullable(),
  archiveNote: z.string().nullable(),
  // IMM-1048 — set only while ARCHIVED by losing a deal: the deal to open, when it was lost, and the status a reopen returns to.
  archivedWithDealLoss: z
    .object({ dealId: refersTo('deal', z.uuid()), lostAt: z.iso.datetime(), restoreStatus: PropertyStatusSchema })
    .nullable(),
  // IMM-405 — reference flags on a sold Objekt (ARCHIVED + SOLD). `isReference`
  // curates the sale as a reusable reference; `publishOnHomepage` is a stored
  // consent for the future homepage feed and is only ever true while
  // `isReference` is true. Both default false and reset to false on de-sale.
  isReference: z.boolean(),
  publishOnHomepage: z.boolean(),
  // null unless `status` is ACTIVE and at least one of `byHand` / `throughSalesPipeline` holds.
  reservation: PropertyReservationSchema.nullable(),
  availableFrom: z.string().nullable(),
  // IMM-589 — the READ shape, deliberately not the write body's. A staged
  // Objekt may hold a blank postcode/city; the publish gate, not this schema,
  // is what demands them.
  address: PropertyDetailAddressSchema,
  areas: PropertyAreasSchema,
  distances: PropertyDistancesSchema,
  building: PropertyBuildingSchema.nullable(),
  energy: PropertyEnergySchema.nullable(),
  descriptions: PropertyDescriptionsResponseSchema,
  pricing: PropertyPricingSchema,
  // IMM-792.4 — null unless marketingType is RENTAL, mirroring pricing.rental
  rentalTerms: PropertyRentalTermsSchema.nullable(),
  additionalCosts: PropertyAdditionalCostsSchema,
  investmentYield: PropertyInvestmentYieldSchema.nullable(),
  // Unified amenity list — backend stores residential vs commercial in
  // separate tables, but on the wire we merge them: residential entries
  // come first in canonical code order, commercial after.
  amenities: PropertyAmenitiesSchema,
  plotDetails: PropertyPlotDetailsSchema.nullable(),
  // Publish-gate fields still missing for this property's object type.
  // Empty when the property is publishable. Derived read-side so the deal
  // detail page can explain why a `Vertrag erhalten` auto-promotion did not
  // activate the Objekt (IMM-171.2), without re-running the gate on the client.
  missingPublishFields: z.array(PublishGateFieldSchema),
  // IMM-792.7 — false while a reserving (DRAFT/ACTIVE/DEFERRED), non-deleted
  // Deal hangs off this property; the identity PATCH then refuses a
  // marketing-type change. Lets the UI state the reason at the action.
  canChangeMarketingType: z.boolean(),
  // IMM-569.8 — the record's own URL plus the URLs of everything filed under it.
  // Additive and never load-bearing: a consumer ignoring it is unaffected. The
  // organisation-addressed targets are built from `organizationId` above, so the
  // workspace-addressed detail read emits usable links too. A link says the
  // resource exists, never that the caller may read it — `documents`, `media`,
  // `dossier` and `valuation` each need a grant the property read itself does not
  // imply.
  links: PropertyLinksSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable(),
  version: z.number().int().positive(),
});

export type PropertyResponse = z.infer<typeof PropertyResponseSchema>;

export const CreatePropertyResponseSchema = PropertyResponseSchema;

export type CreatePropertyResponse = z.infer<typeof CreatePropertyResponseSchema>;

/**
 * Slim list-row shape. Drops every address field the table doesn't
 * render (district, state, country, parcel*, hideExactAddress, lat/long)
 * to keep the list payload small. Detail endpoints return
 * `PropertyResponseSchema`.
 */
export const PropertyListItemAreasSchema = z.object({
  livingArea: z.number().nullable(),
  // `primaryArea` is resolved per category so the list can show one headline
  // figure: APARTMENT → LIVING_AREA, HOUSE → LIVING_AREA (Mehrfamilienhaus →
  // TOTAL_AREA), COMMERCIAL → USABLE_AREA, LAND → PLOT_AREA, GARAGE_PARKING → null.
  primaryArea: z.number().nullable(),
  // IMM-788.6 — the smallest unit the object can be let as (divisible from);
  // stored as the MIN_DIVISIBLE_AREA area row, null for everything else.
  minDivisibleArea: z.number().nullable(),
  rooms: z.number().nullable(),
});

export const PropertyListItemPricingSchema = z.object({
  askingPrice: z.number().nullable(),
  // IMM-788.6 — the rent per m² over the row's primary area, resolved through
  // `resolvePricePerSqm`: the stored figure when the agent typed the per-m²
  // one, derived from `askingPrice` otherwise, null when the basis is absent
  // or zero. RENTAL rows only — a SALE row is always null, because a
  // Kaufpreis/m² is a different figure this feature does not introduce.
  // Resolved for every rental; the „instead of the total" display rule is
  // COMMERCIAL-only and belongs to the surface that renders it.
  pricePerSqm: z.number().nullable(),
  // IMM-792.8 — the Nebenkosten of a RENTAL row, shown beside the Kaltmiete in the
  // list. Always null on a SALE row, whatever the rental table still holds from
  // before a marketing-type switch.
  ancillaryCosts: z.number().nullable(),
  // IMM-1380: the buyer commission statement of a SALE row, null while incomplete; always null on a RENTAL row.
  buyerCommission: CommissionStatementSchema.nullable(),
});

export const PropertyListItemSchema = z.object({
  id: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: refersTo('organization', z.uuid()),
  assignedMemberId: refersTo('member', z.uuid()),
  title: z.string().min(1),
  category: PropertyCategorySchema,
  subtype: PropertySubtypeSchema.nullable(),
  // IMM-551 — the Objekt-Nr., same read-only system-assigned value the detail
  // response carries. On the slim row because the list column is in the default
  // column set (PRD Resolved Decision 3).
  objectNumber: z.string(),
  marketingType: MarketingTypeSchema,
  status: PropertyStatusSchema,
  // IMM-392 — the Archivierungsgrund for the archived-list badge + client-side
  // reason filter. Null unless the row is ARCHIVED with a reason (the free-text
  // note is detail-only and omitted from the slim row).
  archiveReason: ArchiveReasonSchema.nullable(),
  // IMM-405 — drives the „Referenz" badge on the archived list row. `true`
  // only on a sold Objekt the agent has flagged as a reference.
  isReference: z.boolean(),
  publishOnHomepage: z.boolean(),
  // null unless `status` is ACTIVE and at least one of `byHand` / `throughSalesPipeline` holds.
  reservation: PropertyReservationSchema.nullable(),
  // IMM-788.6 — the same value the detail response carries, on the slim row so
  // the list can head a "available from" column for commercial stock.
  availableFrom: z.string().nullable(),
  areas: PropertyListItemAreasSchema,
  pricing: PropertyListItemPricingSchema,
  address: PropertySummaryAddressSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type PropertyListItem = z.infer<typeof PropertyListItemSchema>;

export const ListWorkspaceOrganizationPropertiesPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

export const ListWorkspaceOrganizationPropertiesResponseSchema = z.object({
  data: z.array(PropertyListItemSchema),
});

export type ListWorkspaceOrganizationPropertiesResponse = z.infer<
  typeof ListWorkspaceOrganizationPropertiesResponseSchema
>;

// IMM-20.4 — workspace-addressed Standort-visible list. The path carries only
// the workspace; the visible-Standort set (the caller's affiliated orgs +
// descendants, plus any property assigned to them) is resolved server-side from
// the membership. `organizationId` narrows the result to a single Standort the
// caller can already see; `ids` batch-restricts the same way the org-addressed
// list does. The response is the same slim list DTO as the org-addressed list.
export const ListVisiblePropertiesPathParamsSchema = z.object({
  workspaceId: z.uuid(),
});

export const ListVisiblePropertiesQuerySchema = IdsFilterQuerySchema.extend({
  // Optional free-text filter (IMM-448.4), matched by the SAME repository
  // predicate the ⌘K palette's Objekte source runs — internal title plus the
  // linked address (Straße, PLZ, Ort), case- and diacritic-insensitively. That
  // shared predicate is what makes the palette's „5 von 12" and this list agree.
  // Absent or blank (`?q=`) → no search.
  q: ListSearchTermSchema,
  organizationId: z.uuid().optional(),
  // Narrow to this set of Standorte (a node + its descendants), intersected
  // with the caller's visible set. The frontend computes the subtree id set
  // from the org tree it holds and sends it `?organizationIds=uuid,uuid`. Takes
  // precedence over the single `organizationId` when non-empty; ids outside the
  // visible set are ignored (never widens). Absent / blank → no narrowing.
  organizationIds: commaSeparated(z.uuid()),
});

export type ListVisiblePropertiesQuery = z.infer<typeof ListVisiblePropertiesQuerySchema>;

export const ListVisiblePropertiesResponseSchema = ListWorkspaceOrganizationPropertiesResponseSchema;

export type ListVisiblePropertiesResponse = z.infer<typeof ListVisiblePropertiesResponseSchema>;

export const GetWorkspaceOrganizationPropertyPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const GetWorkspaceOrganizationPropertyResponseSchema = PropertyResponseSchema;

export type GetWorkspaceOrganizationPropertyResponse = z.infer<typeof GetWorkspaceOrganizationPropertyResponseSchema>;

// The workspace-addressed detail read adds where to address the property's
// organisation-scoped READS (IMM-1105): its own Standort when the caller sees
// it, otherwise the Standort of the Deal or Lead that links the caller to it.
// A link opens the property read-only — when this differs from
// `organizationId`, every write answers as for a property the caller cannot see.
export const GetVisiblePropertyResponseSchema = PropertyResponseSchema.extend({
  accessOrganizationId: refersTo('organization', z.uuid()),
});
export type GetVisiblePropertyResponse = z.infer<typeof GetVisiblePropertyResponseSchema>;

// Workspace-addressed single-property read. The board's hierarchical Standort
// visibility (IMM-20.4) can surface a property whose home Standort is not the
// caller's selected one, so the detail read resolves it across the workspace
// rather than 404-ing against the selected organisation.
export const GetWorkspacePropertyPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});

// Section is replaced wholesale (all-or-nothing per the hydration rule):
// the PUT body is the full `PropertyBuildingSchema`, the response echoes
// the refreshed property so the FE can swap the loader cache without a
// follow-up GET.
export const UpdatePropertyBuildingPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const UpdatePropertyBuildingBodySchema = PropertyBuildingSchema;
export type UpdatePropertyBuildingBody = z.infer<typeof UpdatePropertyBuildingBodySchema>;

export const UpdatePropertyBuildingResponseSchema = PropertyResponseSchema;
export type UpdatePropertyBuildingResponse = z.infer<typeof UpdatePropertyBuildingResponseSchema>;

export const UpdatePropertyDescriptionsResponseSchema = PropertyResponseSchema;
export type UpdatePropertyDescriptionsResponse = z.infer<typeof UpdatePropertyDescriptionsResponseSchema>;

// Address is a 1:1 row replaced wholesale: the PUT body is the full
// `PropertyAddressSchema` (every column on `addresses`, including the
// auto-filled lat/long), and the response echoes the refreshed property.
export const UpdatePropertyAddressPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const UpdatePropertyAddressBodySchema = PropertyAddressSchema;
export type UpdatePropertyAddressBody = z.infer<typeof UpdatePropertyAddressBodySchema>;

export const UpdatePropertyAddressResponseSchema = PropertyResponseSchema;
export type UpdatePropertyAddressResponse = z.infer<typeof UpdatePropertyAddressResponseSchema>;

// IMM-167 — identity-level fields editable after creation: the marketing
// type (SALE ↔ RENTAL) and the reference number. The
// reference is workspace-unique (partial index, soft-deletes excluded) so
// a collision 422s `property.property.reference-number-taken`; `null`
// clears it. The response echoes the refreshed property so the FE can swap
// its loader cache without a follow-up GET.
export const UpdatePropertyIdentityPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const UpdatePropertyIdentityBodySchema = z
  .object({
    marketingType: MarketingTypeSchema,
    referenceNumber: z.string().trim().min(1).max(64).nullable(),
    title: z.string().trim().min(1).max(160),
    // IMM-432.3 — the Immobilientyp. `category` is editable only while the
    // property is a DRAFT (IMM-397.2); the server enforces the DRAFT-only +
    // block-if-orphaning rules, so it is optional — published objects and
    // untouched edits omit it. `subtype` is `undefined` to leave unchanged,
    // `null` to clear, or a value from the (possibly new) category; a
    // subtype-only change is unrestricted. The pair is validated below.
    category: PropertyCategorySchema.optional(),
    subtype: PropertySubtypeSchema.nullable().optional(),
    // IMM-1057.2 — `undefined` leaves the Feintyp unchanged, `null` clears it,
    // a value sets it; validated as a pair with the Untertyp.
    commercialFineType: CommercialFineTypeSchema.nullable().optional(),
    // IMM-792.9 — the availability date the letting publish gate (IMM-792.6)
    // requires; IMM-788.4 made it three-state so a commercial edit can leave
    // the stored date untouched: `undefined` leaves it, `null` clears it, a
    // `YYYY-MM-DD` string sets it. Marketing-type-agnostic on the way in;
    // only the gate is rental-only.
    availableFrom: z.iso.date().nullable().optional(),
  })
  .superRefine(refinePropertyImmobilientyp);
export type UpdatePropertyIdentityBody = z.infer<typeof UpdatePropertyIdentityBodySchema>;

export const UpdatePropertyIdentityResponseSchema = PropertyResponseSchema;
export type UpdatePropertyIdentityResponse = z.infer<typeof UpdatePropertyIdentityResponseSchema>;

// Energy mirrors the building shape: section is replaced wholesale and
// the response echoes the refreshed property. IMM-612 — the certificate's
// five fields are conditional on `certificateYear`: required when a
// certificate exists, `null` when the year states there is none. The
// `property_energy_certificate_completeness_check` CHECK constraint
// enforces the same rule at the DB level.
export const UpdatePropertyEnergyPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const UpdatePropertyEnergyBodySchema = PropertyEnergyWriteSchema;
export type UpdatePropertyEnergyBody = z.infer<typeof UpdatePropertyEnergyBodySchema>;

export const UpdatePropertyEnergyResponseSchema = PropertyResponseSchema;
export type UpdatePropertyEnergyResponse = z.infer<typeof UpdatePropertyEnergyResponseSchema>;

// Investment-yield mirrors the building shape: the section is one
// `property_investment_yield` row keyed by `propertyId`, replaced
// wholesale. `PropertyInvestmentYieldSchema` already declares every
// scalar required, and hydration projects to `null` when any column is
// null — so the contract enforces all-or-nothing on the wire too.
export const UpdatePropertyInvestmentYieldPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const UpdatePropertyInvestmentYieldBodySchema = PropertyInvestmentYieldWriteSchema;
export type UpdatePropertyInvestmentYieldBody = z.infer<typeof UpdatePropertyInvestmentYieldBodySchema>;

export const UpdatePropertyInvestmentYieldResponseSchema = PropertyResponseSchema;
export type UpdatePropertyInvestmentYieldResponse = z.infer<typeof UpdatePropertyInvestmentYieldResponseSchema>;

// Plot details mirrors the building shape: section is replaced wholesale
// and the response echoes the refreshed property. `PropertyPlotDetailsSchema`
// already declares every scalar non-nullable, matching the all-or-nothing
// hydration rule.
export const UpdatePropertyPlotDetailsPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const UpdatePropertyPlotDetailsBodySchema = PropertyPlotDetailsSchema;
export type UpdatePropertyPlotDetailsBody = z.infer<typeof UpdatePropertyPlotDetailsBodySchema>;

export const UpdatePropertyPlotDetailsResponseSchema = PropertyResponseSchema;
export type UpdatePropertyPlotDetailsResponse = z.infer<typeof UpdatePropertyPlotDetailsResponseSchema>;

// Amenities are a multi-row child collection — the PUT body is the
// complete desired set, the repo replaces the rows in a TX
// (DELETE+INSERT). An empty array `[]` is valid and clears the section.
export const UpdatePropertyAmenitiesPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const UpdatePropertyAmenitiesBodySchema = PropertyAmenitiesSchema;
export type UpdatePropertyAmenitiesBody = z.infer<typeof UpdatePropertyAmenitiesBodySchema>;

export const UpdatePropertyAmenitiesResponseSchema = PropertyResponseSchema;
export type UpdatePropertyAmenitiesResponse = z.infer<typeof UpdatePropertyAmenitiesResponseSchema>;

// Pricing-sale: one row in `property_pricing_sale`, replaced wholesale
// (single-row upsert). 422 with `property.property.pricing-not-applicable`
// when the property's `marketing_type` is not SALE.
export const UpdatePropertyPricingSalePathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const UpdatePropertyPricingSaleBodySchema = PropertyPricingSaleBodySchema;
export type UpdatePropertyPricingSaleBody = z.infer<typeof UpdatePropertyPricingSaleBodySchema>;

export const UpdatePropertyPricingSaleResponseSchema = PropertyResponseSchema;
export type UpdatePropertyPricingSaleResponse = z.infer<typeof UpdatePropertyPricingSaleResponseSchema>;

// Pricing-rental: same shape contract as pricing-sale, gated on RENTAL.
export const UpdatePropertyPricingRentalPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const UpdatePropertyPricingRentalBodySchema = PropertyPricingRentalBodySchema;
export type UpdatePropertyPricingRentalBody = z.infer<typeof UpdatePropertyPricingRentalBodySchema>;

export const UpdatePropertyPricingRentalResponseSchema = PropertyResponseSchema;
export type UpdatePropertyPricingRentalResponse = z.infer<typeof UpdatePropertyPricingRentalResponseSchema>;

// Rental terms: same shape contract as pricing-rental, gated on RENTAL.
export const UpdatePropertyRentalTermsPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const UpdatePropertyRentalTermsResponseSchema = PropertyResponseSchema;
export type UpdatePropertyRentalTermsResponse = z.infer<typeof UpdatePropertyRentalTermsResponseSchema>;

// Additional costs: multi-row child. The PUT body is the complete desired
// set; the repo DELETEs every existing row and INSERTs the payload in a
// TX. An empty array `[]` clears the section. Available on any property
// regardless of marketing_type.
export const UpdatePropertyAdditionalCostsPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const UpdatePropertyAdditionalCostsBodySchema = PropertyAdditionalCostsSchema;
export type UpdatePropertyAdditionalCostsBody = z.infer<typeof UpdatePropertyAdditionalCostsBodySchema>;

export const UpdatePropertyAdditionalCostsResponseSchema = PropertyResponseSchema;
export type UpdatePropertyAdditionalCostsResponse = z.infer<typeof UpdatePropertyAdditionalCostsResponseSchema>;

// Areas is a multi-row child table keyed by (propertyId, areaType). The
// PUT body is the complete desired set as an array of `{ type, value }`
// (the on-wire shape that also appears on the create body and the
// property response). Empty entries are dropped client-side, so the
// repo can DELETE+INSERT the lot atomically. An empty body (`[]`)
// deletes every row.
export const UpdatePropertyAreasPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const UpdatePropertyAreasBodySchema = PropertyAreasWriteSchema;
export type UpdatePropertyAreasBody = z.infer<typeof UpdatePropertyAreasBodySchema>;

export const UpdatePropertyAreasResponseSchema = PropertyResponseSchema;
export type UpdatePropertyAreasResponse = z.infer<typeof UpdatePropertyAreasResponseSchema>;

// ---------------------------------------------------------------------
// Lifecycle transitions (IMM-163) — activate / deactivate / archive /
// unarchive. Each is a POST with an empty body and echoes the refreshed
// property. The status mutation lands on `properties.status` with an
// append-only `property_status_changes` audit row in the same TX.
//
// `activate` runs the publish gate on a `DRAFT → ACTIVE` transition: the
// publication-ready fields below must all be present, else the request
// fails 422 with `property.property.cannot-activate` and the missing
// field keys in `details.missingFields`. `INACTIVE → ACTIVE` skips the
// gate (the property was published once already). Any other source
// status fails 422 with `property.property.invalid-status-transition`.
//
// The field keys are the stable contract tokens the web app maps to
// localised labels via `packages/i18n`; the primary-area key depends on
// the object type (living area / usable area / total area / plot area /
// parking-space type). `PUBLISH_GATE_FIELDS` is defined above
// `PropertyResponseSchema`, which surfaces the current gap as
// `missingPublishFields`.

export const PropertyLifecyclePathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const ActivatePropertyResponseSchema = PropertyResponseSchema;
export type ActivatePropertyResponse = z.infer<typeof ActivatePropertyResponseSchema>;

export const DeactivatePropertyResponseSchema = PropertyResponseSchema;
export type DeactivatePropertyResponse = z.infer<typeof DeactivatePropertyResponseSchema>;

// IMM-392 — archive / change-archive-reason request body. `reason` is required;
// `note` is optional free text capped at 280 chars (`null` clears it). `OTHER`
// additionally requires a non-empty note — the same rule the domain aggregate
// enforces, mirrored here so a bad request fails at the boundary. The
// marketing-type gate (`SOLD`⇒SALE, `RENTED`⇒RENTAL) is domain-only because the
// body carries no marketing type.
export const ArchivePropertyBodySchema = z
  .object({
    reason: ArchiveReasonSchema,
    note: z.string().trim().max(280).nullable(),
  })
  .superRefine((value, ctx) => {
    if (value.reason === 'OTHER' && (value.note === null || value.note.trim() === '')) {
      ctx.addIssue({
        code: 'custom',
        path: ['note'],
        message: 'A note is required when the archive reason is OTHER',
      });
    }
  });
export type ArchivePropertyBody = z.infer<typeof ArchivePropertyBodySchema>;

// IMM-1048.2 — the archive leaf's body. Both loss fields are optional so today's callers stay valid.
export const ArchivePropertyRequestBodySchema = ArchivePropertyBodySchema.extend({
  lossReason: DealLossReasonSchema.optional(),
  lossNote: z.string().trim().max(1000).nullable().optional(),
});
export type ArchivePropertyRequestBody = z.infer<typeof ArchivePropertyRequestBodySchema>;

export const PropertyArchivePreviewResponseSchema = z.object({
  openDealCount: z.number().int().min(0),
  mayLoseOpenDeals: z.boolean(),
});
export type PropertyArchivePreviewResponse = z.infer<typeof PropertyArchivePreviewResponseSchema>;

export const ArchivePropertyResponseSchema = PropertyResponseSchema;
export type ArchivePropertyResponse = z.infer<typeof ArchivePropertyResponseSchema>;

export const ChangeArchiveReasonResponseSchema = PropertyResponseSchema;

// IMM-405 — set the reference flags on a sold Objekt in one atomic request.
// The body carries the full desired pair so the cascade is atomic: turning the
// reference off sends `publishOnHomepage: false` alongside. `publishOnHomepage`
// may only be true while `isReference` is true — the invariant is rejected here
// at the boundary and re-enforced in the domain aggregate. The sold gate
// (`ARCHIVED` + `SOLD`) carries no marketing state on the body, so it is
// domain-only.
export const UpdatePropertyReferenceSettingsBodySchema = z
  .object({
    isReference: z.boolean(),
    publishOnHomepage: z.boolean(),
  })
  .superRefine((value, ctx) => {
    if (value.publishOnHomepage && !value.isReference) {
      ctx.addIssue({
        code: 'custom',
        path: ['publishOnHomepage'],
        message: 'publishOnHomepage requires isReference to be true',
      });
    }
  });
export type UpdatePropertyReferenceSettingsBody = z.infer<typeof UpdatePropertyReferenceSettingsBodySchema>;

export const UpdatePropertyReferenceSettingsResponseSchema = PropertyResponseSchema;
export type UpdatePropertyReferenceSettingsResponse = z.infer<typeof UpdatePropertyReferenceSettingsResponseSchema>;

// IMM-1145.2 — the desired hand mark; the PUT is idempotent.
export const UpdatePropertyHandReservationBodySchema = z.object({ reservedByHand: z.boolean() }).strict();
export type UpdatePropertyHandReservationBody = z.infer<typeof UpdatePropertyHandReservationBodySchema>;
export const UpdatePropertyHandReservationResponseSchema = PropertyResponseSchema;
export type ChangeArchiveReasonResponse = z.infer<typeof ChangeArchiveReasonResponseSchema>;

export const UnarchivePropertyResponseSchema = PropertyResponseSchema;
export type UnarchivePropertyResponse = z.infer<typeof UnarchivePropertyResponseSchema>;

// IMM-171.4 — gate-free manual staging transitions: DRAFT → IN_ACQUISITION
// (`stage-in-acquisition`) and IN_ACQUISITION → DRAFT (`revert-to-draft`). Any
// other source status fails 422 `property.property.invalid-status-transition`;
// a concurrent transition that moved the status first fails 422
// `property.property.status-conflict`. No publish gate — these two pre-ACTIVE
// states are freely interchangeable.
export const StageInAcquisitionResponseSchema = PropertyResponseSchema;
export type StageInAcquisitionResponse = z.infer<typeof StageInAcquisitionResponseSchema>;

export const RevertToDraftResponseSchema = PropertyResponseSchema;
export type RevertToDraftResponse = z.infer<typeof RevertToDraftResponseSchema>;

// ---------------------------------------------------------------------
// Reassignment (IMM-165). Repoints the property's primary assignment to
// another workspace member; the route is gated on
// `organization:property:property-reassign`. The target is validated to be an
// active member of the same workspace — an unknown / out-of-scope /
// deactivated id fails 404. Echoes the refreshed property.
export const ReassignPropertyPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const ReassignPropertyBodySchema = z.object({
  assignedMemberId: z.uuid(),
});
export type ReassignPropertyBody = z.infer<typeof ReassignPropertyBodySchema>;

export const ReassignPropertyResponseSchema = PropertyResponseSchema;
export type ReassignPropertyResponse = z.infer<typeof ReassignPropertyResponseSchema>;

// Soft-delete (IMM-165). Sets `deleted_at`; every read path filters
// `deleted_at IS NULL`, so the property leaves every list (including the
// archived view) and subsequent reads 404. Reversible only via
// engineering / support tooling — there is no in-product undo. Gated on
// `organization:property:property-delete`.
export const SoftDeletePropertyPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

// Optional commit-time status guard (IMM-171.3). When present, the delete is
// refused with `property.property.delete-status-conflict` (422) unless the
// property is still in `expectedStatus` at the load inside the handler's unit
// of work — the post-Verloren cleanup passes `expectedStatus=IN_ACQUISITION`.
// Omitted → today's unconditional soft delete, unchanged.
export const SoftDeletePropertyQuerySchema = z.object({
  expectedStatus: PropertyStatusSchema.optional(),
});
export type SoftDeletePropertyQuery = z.infer<typeof SoftDeletePropertyQuerySchema>;

export const SoftDeletePropertyResponseSchema = z.object({
  id: z.uuid(),
});
export type SoftDeletePropertyResponse = z.infer<typeof SoftDeletePropertyResponseSchema>;

// Admin reads carry plain workspace + organisation **ids**, not names.
// The admin app resolves names client-side via `adminOrganizations.list`
// (which already exposes `workspaceName` per organisation), so the
// property module never reaches into identity tables. See ADR 0008.
//
// `.extend`, NOT an alias: the customer lists share `PropertyListItemSchema`,
// and the operator-only `isTest` mark (IMM-593.3) must not leak onto a
// customer-facing surface.
export const AdminPropertyListItemSchema = PropertyListItemSchema.omit({ reservation: true, subtype: true }).extend({
  // Whether the Objekt's workspace is a testing environment — the operator
  // list's `Test` badge, resolved server-side so no second lookup is needed.
  isTest: z.boolean(),
});

export type AdminPropertyListItem = z.infer<typeof AdminPropertyListItemSchema>;

// The operator list's only parameter: the test-workspace filter (IMM-593.3),
// same name and default as the identity lists.
export const ListAdminPropertiesQuerySchema = IncludeTestWorkspacesQuerySchema;
export type ListAdminPropertiesQuery = z.infer<typeof ListAdminPropertiesQuerySchema>;

export const ListAdminPropertiesResponseSchema = z.object({
  data: z.array(AdminPropertyListItemSchema),
});

export type ListAdminPropertiesResponse = z.infer<typeof ListAdminPropertiesResponseSchema>;

export const GetAdminPropertyPathParamsSchema = z.object({
  id: z.uuid(),
});

export const ReadAdminPropertyDocumentPathParamsSchema = z.object({
  id: z.uuid(),
  documentId: z.uuid(),
});

// The operator detail read (IMM-1096.3) also returns a soft-deleted Objekt,
// with who deleted it. Extended, never the base: PropertyResponseSchema is the
// customer + published shape and must not gain the deleter.
export const GetAdminPropertyResponseSchema = PropertyResponseSchema.extend({
  deletedBy: DeletedBySchema.nullable(),
});

export type GetAdminPropertyResponse = z.infer<typeof GetAdminPropertyResponseSchema>;

// ---------------------------------------------------------------------
// IMM-6 — Property↔Contact link (per-link role).
//
// Closed enum per PRD §3 (Resolved Decision). Internal storage stays
// UPPER_CASE per AGENTS.md's union-type convention; the user-facing
// labels are German and looked up via `EnumTranslation<...>` in
// `packages/i18n`. Distinct from `ContactType` (the 5-value
// contact-categorisation tag set on the contact itself).
// FINANCIER sits before OTHER: array order is the dialogs' role order.
export const PROPERTY_CONTACT_ROLES = [
  'OWNER',
  'PURCHASE_PROSPECT',
  'RENTAL_PROSPECT',
  'TENANT',
  'PROPERTY_MANAGER',
  'EXTERNAL_BROKER',
  'NOTARY',
  'APPRAISER',
  'CRAFTSMAN',
  'FINANCIER',
  'OTHER',
] as const;

export const PropertyContactRoleSchema = z.enum(PROPERTY_CONTACT_ROLES);
export type PropertyContactRole = z.infer<typeof PropertyContactRoleSchema>;

// Prospect roles are marketing-type-bound (IMM-6.3): a buyer-prospect
// only makes sense on a SALE listing, a renter-prospect only on a RENTAL
// one. The opposite prospect for a given marketing type is the one we
// must not offer / must reject on create+role-change.
const OPPOSITE_PROSPECT_ROLE: Record<MarketingType, PropertyContactRole> = {
  SALE: 'RENTAL_PROSPECT',
  RENTAL: 'PURCHASE_PROSPECT',
};

/**
 * True when `role` is the prospect role that does not match `marketingType`
 * (e.g. `RENTAL_PROSPECT` on a SALE property). Non-prospect roles are
 * always allowed. Used to filter the offered roles in the link/edit forms
 * and as the backend's defence-in-depth guard on create/role-change.
 */
export function isProspectRoleMismatch(role: PropertyContactRole, marketingType: MarketingType): boolean {
  return role === OPPOSITE_PROSPECT_ROLE[marketingType];
}

/**
 * The roles offerable on a property of the given marketing type: every
 * role except the mismatched prospect one (IMM-6.3).
 */
export function offerableContactRoles(marketingType: MarketingType): ReadonlyArray<PropertyContactRole> {
  return PROPERTY_CONTACT_ROLES.filter((role) => !isProspectRoleMismatch(role, marketingType));
}

/**
 * Wire shape of one row on the property's "Kontakte" tab — slice 2 of
 * IMM-6. Re-uses the contact's slim list-item projection and tacks the
 * per-link `role` on top. The view is a join (`property_contacts` ×
 * `contacts`) so a contact carrying multiple roles on the same property
 * surfaces once per role.
 */
export const PropertyContactSchema = ContactListItemSchema.extend({
  role: PropertyContactRoleSchema,
  note: z.string().nullable(),
  linkUpdatedAt: z.iso.datetime(),
});

export type PropertyContact = z.infer<typeof PropertyContactSchema>;

export const ListPropertyContactsPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

// Optional `?role=OWNER` filter — restricts the result to links of that role.
// Absent means "no constraint" (all linked contacts). The deal add-owner
// picker uses `role=OWNER` to offer only the Objekt's owners.
export const ListPropertyContactsQuerySchema = z.object({
  role: PropertyContactRoleSchema.optional(),
});

export const ListPropertyContactsResponseSchema = z.object({
  data: z.array(PropertyContactSchema),
});

export type ListPropertyContactsQuery = z.infer<typeof ListPropertyContactsQuerySchema>;
export type ListPropertyContactsResponse = z.infer<typeof ListPropertyContactsResponseSchema>;

/**
 * Wire shape of one row on the contact's "Immobilien" section —
 * slice 3 of IMM-6. Reuses the property slim list-item projection
 * and tacks the per-link `role` on top. Same role enum
 * ({@link PropertyContactRoleSchema}) as the inverse property→contact
 * surface — the join entity is symmetric.
 */
export const ContactPropertySchema = PropertyListItemSchema.extend({
  role: PropertyContactRoleSchema,
  note: z.string().nullable(),
  linkUpdatedAt: z.iso.datetime(),
});

export type ContactProperty = z.infer<typeof ContactPropertySchema>;

export const ListContactPropertiesPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const ListContactPropertiesResponseSchema = z.object({
  data: z.array(ContactPropertySchema),
});

export type ListContactPropertiesResponse = z.infer<typeof ListContactPropertiesResponseSchema>;

/**
 * Attach an existing workspace contact to a property under a given
 * role — drives the "Kontakt verknüpfen" flow on the property detail
 * page's "Kontakte" tab. Slice 1's create-property already accepts an
 * `ownerContactId` for the initial Eigentümer; this endpoint is the
 * post-create equivalent for every other role plus additional
 * Eigentümer.
 */
export const AttachPropertyContactPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

// `note` is the optional per-link free-text colour (PRD Resolved Decision
// §12, max 500 chars). Nullish so the client may omit it or send `null`.
export const AttachPropertyContactBodySchema = z.object({
  contactId: z.uuid(),
  contactRole: PropertyContactRoleSchema,
  note: z.string().max(500).nullish(),
});

export type AttachPropertyContactBody = z.infer<typeof AttachPropertyContactBodySchema>;

export const AttachPropertyContactResponseSchema = PropertyContactSchema;

export type AttachPropertyContactResponse = z.infer<typeof AttachPropertyContactResponseSchema>;

/**
 * Edit one existing property↔contact link — the edit-link flow
 * (IMM-6.2). The link is addressed by its full triple in the
 * path (`:contactId/:contactRole` = the current role), because a contact
 * may hold several roles on the same property. The body carries the *new*
 * role and note. Re-using a `(contact, property, role)` triple that
 * already exists is rejected with `property.property-contact.already-linked`
 * (422); a missing link is `property.property-contact.not-linked` (404).
 */
export const UpdatePropertyContactPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
  contactId: z.uuid(),
  contactRole: PropertyContactRoleSchema,
});

export const UpdatePropertyContactBodySchema = z.object({
  contactRole: PropertyContactRoleSchema,
  note: z.string().max(500).nullable(),
});

export type UpdatePropertyContactBody = z.infer<typeof UpdatePropertyContactBodySchema>;

export const UpdatePropertyContactResponseSchema = PropertyContactSchema;

export type UpdatePropertyContactResponse = z.infer<typeof UpdatePropertyContactResponseSchema>;

/**
 * Remove one property↔contact link — the remove-link flow
 * (IMM-6.2). Addressed by its full triple in the path. Removing a link
 * that no longer exists yields `property.property-contact.not-linked`
 * (404) rather than a 500, so a double-click is safe.
 */
export const DetachPropertyContactPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
  contactId: z.uuid(),
  contactRole: PropertyContactRoleSchema,
});

export const DetachPropertyContactResponseSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  contactId: refersTo('contact', z.uuid()),
  contactRole: PropertyContactRoleSchema,
});

export type DetachPropertyContactResponse = z.infer<typeof DetachPropertyContactResponseSchema>;

// Cross-Standort reassignment (IMM-20.4 AC22). Workspace-addressed:
// `POST /v1/workspaces/:workspaceId/properties/:id/standort`. The body carries the
// target organisation; the handler's authz rule decides whether the move is allowed.
export const ReassignPropertyStandortPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});
export type ReassignPropertyStandortPathParams = z.infer<typeof ReassignPropertyStandortPathParamsSchema>;

export const ReassignPropertyStandortBodySchema = z
  .object({
    organizationId: z.uuid(),
  })
  .strict();
export type ReassignPropertyStandortBody = z.infer<typeof ReassignPropertyStandortBodySchema>;

export const ReassignPropertyStandortResponseSchema = PropertyResponseSchema;
export type ReassignPropertyStandortResponse = z.infer<typeof ReassignPropertyStandortResponseSchema>;
